import { useCallback } from 'react'

type AnalyticsProperties = Record<string, unknown>

type GtagWindow = Window & {
  gtag?: (...args: unknown[]) => void
  dataLayer?: unknown[]
}

type PostHogClient = {
  __loaded?: boolean
  init: (
    apiKey: string,
    options: {
      api_host: string
      autocapture?: boolean
      capture_exceptions?: boolean | Record<string, unknown>
      session_recording: { maskAllInputs: boolean }
      release?: string
    }
  ) => void
  capture: (eventName: string, properties?: AnalyticsProperties) => void
  captureException: (error: unknown, additionalProperties?: AnalyticsProperties) => void
}

const POSTHOG_API_KEY = 'phc_9Oa2QJzhA5V4ZMADbkj5POZOEnwhrNgKAhKBgQNdxkY'
const POSTHOG_HOST = 'https://us.i.posthog.com'

let posthogClientPromise: Promise<PostHogClient> | null = null

function isAnalyticsEnabledForCurrentWebHost(): boolean {
  if (process.env.EXPO_PUBLIC_APP_ENV !== 'production') return false
  if (typeof window === 'undefined') return false

  return (
    window.location.hostname === 'turbinago.com' || window.location.hostname === 'www.turbinago.com'
  )
}

function getGtagClient(): ((...args: unknown[]) => void) | null {
  if (typeof window === 'undefined') return null

  const win = window as GtagWindow
  if (typeof win.gtag === 'function') {
    return (...args: unknown[]) => {
      win.gtag?.(...args)
    }
  }

  if (!Array.isArray(win.dataLayer)) {
    win.dataLayer = []
  }

  return (...args: unknown[]) => {
    win.dataLayer?.push(args)
  }
}

function withDefaultAnalyticsContext(properties?: AnalyticsProperties): AnalyticsProperties {
  if (typeof window === 'undefined') {
    return {
      app_env: process.env.EXPO_PUBLIC_APP_ENV ?? 'unknown',
      ...properties,
    }
  }

  return {
    app_env: process.env.EXPO_PUBLIC_APP_ENV ?? 'unknown',
    host: window.location.hostname,
    page_path: window.location.pathname,
    page_location: window.location.href,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
    locale: typeof document !== 'undefined' ? document.documentElement.lang : undefined,
    ...properties,
  }
}

export async function getPostHogClient(): Promise<PostHogClient> {
  if (posthogClientPromise) {
    return posthogClientPromise
  }

  posthogClientPromise = import('posthog-js').then(({ default: posthog }) => {
    const client = posthog as PostHogClient

    if (!client.__loaded) {
      client.init(POSTHOG_API_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: false,
        capture_exceptions: true,
        session_recording: { maskAllInputs: true },
        release: process.env.EXPO_PUBLIC_RELEASE_NAME,
      })
    }

    return client
  })

  return posthogClientPromise
}

export function useAnalytics() {
  const capture = useCallback((eventName: string, properties?: AnalyticsProperties) => {
    if (!isAnalyticsEnabledForCurrentWebHost()) return

    const enrichedProperties = withDefaultAnalyticsContext(properties)

    const gtag = getGtagClient()
    gtag?.('event', eventName, enrichedProperties)

    void getPostHogClient()
      .then((posthog) => {
        posthog.capture(eventName, enrichedProperties)
      })
      .catch(() => {})
  }, [])

  return { capture }
}

export function reportError(error: unknown, properties?: AnalyticsProperties): void {
  if (!isAnalyticsEnabledForCurrentWebHost()) return

  const enrichedProperties = withDefaultAnalyticsContext(properties)

  void getPostHogClient()
    .then((posthog) => {
      posthog.captureException(error, enrichedProperties)
    })
    .catch(() => {})
}
